import React from "react";
import { useTranslation } from "react-i18next";
import { Grid, Icon, Popup } from "semantic-ui-react";

import { useIsOverflowing, useShowPriceInVat, useTyrePositionName } from "hooks";
import { TYRE_POSITIONS } from "models";
import "modules/AppointmentDetails/components/Interventions/components/Replacements/components/ReplacementsTable/ReplacementsTable.scss";
import { getAxleName, getFullTyreDescription, getTyreSize } from "modules/AppointmentDetails/components/VehicleTyres/utils";
import { useAppointmentLocation } from "modules/AppointmentDetails/hooks";
import { getPriceInVAT, getTyreSeasonIcon } from "utils/common";
import { formatPrice } from "utils/formatPrice";

import type { DealerLocation, VehicleTyreReplacement } from "models";

type ReplacementsTableProps = {
  replacements: VehicleTyreReplacement[];
  unlinkReplacementMutation: (replacementId: number) => void;
};

type ReplacementRowProps = {
  replacement: VehicleTyreReplacement;
  showPriceInVat: boolean;
  location: DealerLocation | undefined;
  unlinkReplacementMutation: (replacementId: number) => void;
};

const ReplacementRow = ({ replacement, showPriceInVat, location, unlinkReplacementMutation }: ReplacementRowProps) => {
  const { ref: typeRef, isOverflowing: isTypeOverflowing } = useIsOverflowing<HTMLParagraphElement>();

  const position = useTyrePositionName(replacement.position);
  const description = getFullTyreDescription(replacement.tyre, location?.is_tyre_net_enabled);

  return (
    <Grid.Row key={replacement.id}>
      <Grid.Column width={2}>{position}</Grid.Column>
      <Grid.Column width={2}>
        <Icon className={getTyreSeasonIcon(replacement.tyre?.season)} /> {getTyreSize(replacement.tyre)}
      </Grid.Column>
      <Grid.Column width={4}>
        <Popup hoverable disabled={!isTypeOverflowing} content={description} trigger={<p ref={typeRef}>{description}</p>} />
      </Grid.Column>
      <Grid.Column width={2}>{replacement.tyre?.manufacturer ?? ""}</Grid.Column>
      <Grid.Column width={2}>{"€ " + (showPriceInVat ? formatPrice(getPriceInVAT(replacement.price, location)) : formatPrice(replacement.price))}</Grid.Column>
      <Grid.Column width={4} textAlign="right">
        {!replacement.mounted && (
          <div className="unlink-action" onClick={() => unlinkReplacementMutation(replacement.id)}>
            <Icon className="link simple slash" />
          </div>
        )}
      </Grid.Column>
    </Grid.Row>
  );
};

export const ReplacementsTable = ({ replacements, unlinkReplacementMutation }: ReplacementsTableProps) => {
  const { t } = useTranslation();

  const showPriceInVat = useShowPriceInVat();
  const appointmentLocation = useAppointmentLocation();

  const replacementsByAxle = Map.groupBy(replacements, r => r.axle);
  let truckSpareIndex = -1;
  for (const [axle, replacements] of replacementsByAxle) {
    if (replacements.some(r => r.position === TYRE_POSITIONS.TruckSpare)) {
      truckSpareIndex = axle;
      break;
    }
  }

  return (
    <div className="ReplacementsTable">
      <Grid>
        <Grid.Row className="header">
          <Grid.Column width={2}>{t("v8_position", "Position")}</Grid.Column>
          <Grid.Column width={2}>{t("v8_size", "Size")}</Grid.Column>
          <Grid.Column width={4}>{t("v8_type", "Type")}</Grid.Column>
          <Grid.Column width={2}>{t("v8_brand", "Brand")}</Grid.Column>
          <Grid.Column width={2}>{t("v8_price", "Price")}</Grid.Column>
          <Grid.Column width={4} />
        </Grid.Row>
        {Array.from(replacementsByAxle, ([axle, replacements]) => (
          <React.Fragment key={"axle-" + axle}>
            {axle > 0 && replacements.length > 0 && (
              <Grid.Row className="axle-header">
                <Grid.Column width={16}>
                  <p>{getAxleName(replacements[0], truckSpareIndex, t)}</p>
                </Grid.Column>
              </Grid.Row>
            )}
            {replacements.map(replacement => (
              <ReplacementRow
                key={replacement.id}
                replacement={replacement}
                unlinkReplacementMutation={unlinkReplacementMutation}
                location={appointmentLocation}
                showPriceInVat={showPriceInVat}
              />
            ))}
          </React.Fragment>
        ))}
      </Grid>
    </div>
  );
};
